import { Toaster, toast as sonnerToast } from 'sonner'
import { CheckCircle2, AlertTriangle, AlertCircle, Info, X } from 'lucide-react'
import type { ReactNode } from 'react'

export type ToastVariant = 'success' | 'warning' | 'danger' | 'info'

export interface ToastOptions {
  description?: ReactNode
  /** Action link shown inline; clicking auto-dismisses. */
  action?: { label: string; onClick: () => void }
  /** Override auto-dismiss. Default: success/info 5s, warning/danger never. */
  duration?: number
}

const ICONS: Record<ToastVariant, React.ComponentType<{ size?: number; className?: string }>> = {
  success: CheckCircle2,
  warning: AlertTriangle,
  danger: AlertCircle,
  info: Info,
}

const ACCENT_CLASS: Record<ToastVariant, string> = {
  success: 'before:bg-success-dot',
  warning: 'before:bg-warning-dot',
  danger: 'before:bg-danger-dot',
  info: 'before:bg-info-dot',
}

const ICON_CLASS: Record<ToastVariant, string> = {
  success: 'text-success-text',
  warning: 'text-warning-text',
  danger: 'text-danger-text',
  info: 'text-info-text',
}

const DEFAULT_DURATION: Record<ToastVariant, number | undefined> = {
  success: 5000,
  info: 5000,
  warning: Infinity, // stay until dismissed
  danger: Infinity,
}

/**
 * Mount once near the app root (inside ThemeProvider). Toasts stack
 * bottom-right, max 4 simultaneously (sonner default honored).
 */
export function ToastContainer() {
  return (
    <Toaster
      position="bottom-right"
      visibleToasts={4}
      closeButton
      toastOptions={{
        classNames: {
          toast:
            'relative rounded-md border border-border-default bg-surface text-primary shadow-md px-3 py-2.5 pl-4 text-sm flex gap-2 items-start ' +
            "before:content-[''] before:absolute before:left-0 before:top-0 before:bottom-0 before:w-[3px] before:rounded-l-md",
          title: 'font-semibold text-sm text-primary',
          description: 'text-xs text-secondary mt-0.5',
          actionButton:
            'text-xs font-medium text-accent-text hover:underline cursor-pointer bg-transparent border-none',
          closeButton:
            'text-tertiary hover:text-primary hover:bg-surface-hover rounded-xs h-5 w-5 inline-flex items-center justify-center cursor-pointer',
        },
      }}
    />
  )
}

/** Imperative toast API — call from event handlers, mutations, etc. */
// eslint-disable-next-line react-refresh/only-export-components
export const toast = {
  success(title: string, opts?: ToastOptions) {
    return fire('success', title, opts)
  },
  warning(title: string, opts?: ToastOptions) {
    return fire('warning', title, opts)
  },
  danger(title: string, opts?: ToastOptions) {
    return fire('danger', title, opts)
  },
  info(title: string, opts?: ToastOptions) {
    return fire('info', title, opts)
  },
  dismiss(id?: string | number) {
    sonnerToast.dismiss(id)
  },
}

function fire(variant: ToastVariant, title: string, opts?: ToastOptions) {
  const Icon = ICONS[variant]
  const duration = opts?.duration ?? DEFAULT_DURATION[variant]
  return sonnerToast.custom(
    (id) => (
      <div className={`${ACCENT_CLASS[variant]} w-full`}>
        <Icon size={16} className={`mt-0.5 flex-shrink-0 ${ICON_CLASS[variant]}`} />
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-sm text-primary">{title}</div>
          {opts?.description ? (
            <div className="text-xs text-secondary mt-0.5">{opts.description}</div>
          ) : null}
          {opts?.action ? (
            <button
              type="button"
              onClick={() => {
                opts.action!.onClick()
                sonnerToast.dismiss(id)
              }}
              className="mt-1 cursor-pointer border-none bg-transparent p-0 text-xs font-medium text-accent-text hover:underline"
            >
              {opts.action.label}
            </button>
          ) : null}
        </div>
        {duration === Infinity ? (
          <button
            type="button"
            aria-label="Dismiss"
            onClick={() => sonnerToast.dismiss(id)}
            className="ml-1 flex-shrink-0 cursor-pointer rounded border-none bg-transparent p-0.5 text-tertiary hover:text-primary"
          >
            <X size={13} />
          </button>
        ) : null}
      </div>
    ),
    { duration },
  )
}
